import React, { useEffect, useState } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { DecoyEffect } from "../../../../translations/experiments/DecoyEffect";
import { colors, commonStyles } from "../../../../components/colors";

// Corporate design chart colors for relative bars
const pommesColors = [colors.lichtBlau, colors.sienna];
const drinkColors = [colors.darkGreen, colors.ocker];

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    padding: "32px 32px 16px 32px",
    margin: "32px auto 0 auto",
    minWidth: 420,
    maxWidth: 650,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 220
};

const chartStyle: React.CSSProperties = {
    minWidth: 280,
    maxWidth: 400,
    width: "100%",
    flex: 1
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                background: colors.white,
                border: `1px solid ${colors.silver}`,
                padding: 10,
                borderRadius: 8,
                fontSize: 15,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <strong style={{ color: colors.brillantBlue }}>{label}</strong>
                <br />
                {payload.map((p: any) => (
                    <div key={p.dataKey} style={{ color: p.color, fontWeight: 500 }}>
                        {p.name}: {p.value}%
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

// Custom label that shows inside the bar for 100%, above for other values
const CustomBarLabel = (props: any) => {
    const { x, y, width, value, height } = props;
    // Bar is 100%: label inside, smaller, not bold
    if (value >= 100) {
        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#fff"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="13"
            >
                {value}%
            </text>
        );
    }
    // Above bar: regular style
    return (
        <text
            x={x + width / 2}
            y={y - 8}
            fill={colors.brillantBlue}
            textAnchor="middle"
            fontSize="16"
            fontWeight="bold"
        >
            {value}%
        </text>
    );
};

export const DecoyEffectCompareScreenComponent = () => {
    const { getGroupData } = useSession();
    const { language } = useLanguage();
    const txt = DecoyEffect[language].screens;

    const [pommesPercents, setPommesPercents] = useState<[number, number]>([0, 0]);
    const [drinkPercents, setDrinkPercents] = useState<[number, number]>([0, 0]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            let g1_klein = 0, g1_groß = 0;
            let g2_klein = 0, g2_groß = 0;
            let d1_small = 0, d1_large = 0;
            let d2_small = 0, d2_large = 0;

            data?.forEach((dat: any) => {
                const obj = dat.DecoyEffect;
                if (obj) {
                    if (obj["0"]?.selections?.length > 0) {
                        const choice = obj["0"].selections[obj["0"].selections.length - 1].choice;
                        if (choice === 1) g1_klein++;
                        if (choice === 2) g1_groß++;
                    }
                    if (obj["2"]?.selections?.length > 0) {
                        const choice = obj["2"].selections[obj["2"].selections.length - 1].choice;
                        if (choice === 1) g2_klein++;
                        if (choice === 3) g2_groß++;
                    }
                    if (obj["1"]?.selections?.length > 0) {
                        const choice = obj["1"].selections[obj["1"].selections.length - 1].choice;
                        if (choice === 1) d1_small++;
                        if (choice === 3) d1_large++;
                    }
                    if (obj["3"]?.selections?.length > 0) {
                        const choice = obj["3"].selections[obj["3"].selections.length - 1].choice;
                        if (choice === 1) d2_small++;
                        if (choice === 2) d2_large++;
                    }
                }
            });

            const g1_total = g1_klein + g1_groß;
            const g2_total = g2_klein + g2_groß;
            const pommesPercents: [number, number] = [
                g1_total > 0 ? Math.round((g1_groß / g1_total) * 100) : 0,
                g2_total > 0 ? Math.round((g2_groß / g2_total) * 100) : 0
            ];

            const d1_total = d1_small + d1_large;
            const d2_total = d2_small + d2_large;
            const drinkPercents: [number, number] = [
                d1_total > 0 ? Math.round((d1_large / d1_total) * 100) : 0,
                d2_total > 0 ? Math.round((d2_large / d2_total) * 100) : 0
            ];

            setPommesPercents(pommesPercents);
            setDrinkPercents(drinkPercents);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>{txt.resultsRelative.loading}</div>;
    }

    const pommesBars = [
        [
            { name: txt.fries1.optionSmall, Prozent: 100 - pommesPercents[0], color: pommesColors[0] },
            { name: txt.fries1.optionLarge, Prozent: pommesPercents[0], color: pommesColors[1] }
        ],
        [
            { name: txt.fries2.optionSmall, Prozent: 100 - pommesPercents[1], color: pommesColors[0] },
            { name: txt.fries2.optionLarge, Prozent: pommesPercents[1], color: pommesColors[1] }
        ]
    ];

    const drinkBars = [
        [
            { name: txt.drink2.optionSmall, Prozent: 100 - drinkPercents[0], color: drinkColors[0] },
            { name: txt.drink2.optionLarge, Prozent: drinkPercents[0], color: drinkColors[1] }
        ],
        [
            { name: txt.drink2.optionSmall, Prozent: 100 - drinkPercents[1], color: drinkColors[0] },
            { name: txt.drink2.optionLarge, Prozent: drinkPercents[1], color: drinkColors[1] }
        ]
    ];

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                marginBottom: 16,
                fontSize: 28,
                fontWeight: 700
            }}>
                {txt.resultsRelative.headline}
            </h2>
            {/* Pommes Vergleich */}
            <div style={cardStyle}>
                <h3 style={{
                    ...commonStyles.heading,
                    marginBottom: 8,
                    fontSize: 20
                }}>
                    {txt.resultsRelative.pommes}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 16, color: colors.textMedium }}>
                        <span>{txt.resultsRelative.pommesLegend[0]}</span>
                        <span style={{ marginLeft: 12 }}>{txt.resultsRelative.pommesLegend[1]}</span>
                    </span>
                </h3>
                <div style={{ display: "flex", gap: 48, justifyContent: "center" }}>
                    <div style={chartStyle}>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={pommesBars[0]}>
                                <XAxis dataKey="name" tick={{ fontSize: 16 }} />
                                <YAxis domain={[0, 100]} allowDecimals={false} tick={{ fontSize: 16 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="Prozent" barSize={38}>
                                    {pommesBars[0].map((entry, idx) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                    <LabelList dataKey="Prozent" content={CustomBarLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <div style={{ textAlign: "center", marginTop: 8, fontSize: 15, color: colors.brillantBlue }}>
                            <b>{txt.resultsRelative.noDecoyLabel}</b>
                        </div>
                    </div>
                    <div style={chartStyle}>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={pommesBars[1]}>
                                <XAxis dataKey="name" tick={{ fontSize: 16 }} />
                                <YAxis domain={[0, 100]} allowDecimals={false} tick={{ fontSize: 16 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="Prozent" barSize={38}>
                                    {pommesBars[1].map((entry, idx) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                    <LabelList dataKey="Prozent" content={CustomBarLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <div style={{ textAlign: "center", marginTop: 8, fontSize: 15, color: colors.brillantBlue }}>
                            <b>{txt.resultsRelative.decoyLabel}</b>
                        </div>
                    </div>
                </div>
            </div>
            {/* Getränke Vergleich */}
            <div style={cardStyle}>
                <h3 style={{
                    ...commonStyles.heading,
                    marginBottom: 8,
                    fontSize: 20
                }}>
                    {txt.resultsRelative.drinks}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 16, color: colors.textMedium }}>
                        <span>{txt.resultsRelative.drinksLegend[0]}</span>
                        <span style={{ marginLeft: 12 }}>{txt.resultsRelative.drinksLegend[1]}</span>
                    </span>
                </h3>
                <div style={{ display: "flex", gap: 48, justifyContent: "center" }}>
                    <div style={chartStyle}>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={drinkBars[0]}>
                                <XAxis dataKey="name" tick={{ fontSize: 16 }} />
                                <YAxis domain={[0, 100]} allowDecimals={false} tick={{ fontSize: 16 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="Prozent" barSize={38}>
                                    {drinkBars[0].map((entry, idx) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                    <LabelList dataKey="Prozent" content={CustomBarLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <div style={{ textAlign: "center", marginTop: 8, fontSize: 15, color: colors.brillantBlue }}>
                            <b>{txt.resultsRelative.noDecoyLabel}</b>
                        </div>
                    </div>
                    <div style={chartStyle}>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={drinkBars[1]}>
                                <XAxis dataKey="name" tick={{ fontSize: 16 }} />
                                <YAxis domain={[0, 100]} allowDecimals={false} tick={{ fontSize: 16 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="Prozent" barSize={38}>
                                    {drinkBars[1].map((entry, idx) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                    <LabelList dataKey="Prozent" content={CustomBarLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <div style={{ textAlign: "center", marginTop: 8, fontSize: 15, color: colors.brillantBlue }}>
                            <b>{txt.resultsRelative.decoyLabel}</b>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{
                marginTop: 28,
                textAlign: "center",
                fontSize: 15,
                background: colors.backgroundLight,
                borderRadius: 10,
                padding: "14px 12px",
                maxWidth: 650,
                marginLeft: "auto",
                marginRight: "auto",
                color: colors.textMedium
            }}>
                <span dangerouslySetInnerHTML={{ __html: txt.resultsRelative.ignoreMedium }} />
            </div>
        </div>
    );
};

export const ResultScreenRelative: ScreenConfig = {
    component: DecoyEffectCompareScreenComponent,
    defaultResult: {},
    canContinueDefault: true,
};